import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { storage } from 'utils/firebase-config';

/**
 * Session cache for Firebase Storage download URLs (and the images' measured aspect
 * ratios), keyed by storage path.
 *
 * MessageBox remounts on every room switch, and its effect used to call `getDownloadURL`
 * again for each attachment — a network round-trip before `src` could even be set, then a
 * second `new Image()` pass to measure the aspect ratio. That's the image "re-flash".
 *
 * The important part is `getCachedAttachmentUrl`, which is **synchronous**: a memoized
 * *promise* would still defer to a microtask, leaving one render with no `src` — i.e. the
 * flash survives. A sync getter lets the component seed its state during the first render.
 *
 * In-memory only, deliberately. Download URLs carry a `?token=` that doesn't expire, but
 * it *can* be revoked; persisting these would mean serving permanently broken URLs.
 */

/** How long a 404 is trusted before we look again (an upload may land slightly late). */
const MISSING_TTL_MS = 60 * 1000;

const urls = new Map<string, string>();
const inFlight = new Map<string, Promise<string | null>>();
const missing = new Map<string, number>();
const ratios = new Map<string, number>();

/**
 * Attachment ids look like "778-dR2KdtpbKSEjxKf", where the prefix is the room folder.
 * (This mirrors what getImages() always did — it ignored its `roomId` argument.)
 */
export const attachmentPath = (attachmentId: string): string =>
  `/${attachmentId.split('-')[0]}/${attachmentId}`;

/** Synchronous cache read — the whole point of this module. */
export const getCachedAttachmentUrl = (path: string): string | undefined => urls.get(path);

export const getCachedAspectRatio = (url: string): number | undefined => ratios.get(url);

export const setCachedAspectRatio = (url: string, ratio: number): void => {
  if (Number.isFinite(ratio) && ratio > 0) ratios.set(url, ratio);
};

/**
 * Resolve a download URL, de-duplicating concurrent requests for the same path (the same
 * image can render in the message list and the thread panel at once).
 *
 * Returns `null` when the object doesn't exist. Other errors reject so the caller can
 * retry — a transient network failure must not be cached as "missing".
 */
export const resolveAttachmentUrl = (path: string): Promise<string | null> => {
  const cached = urls.get(path);
  if (cached) return Promise.resolve(cached);

  const missedAt = missing.get(path);
  if (missedAt !== undefined) {
    if (Date.now() - missedAt < MISSING_TTL_MS) return Promise.resolve(null);
    missing.delete(path);
  }

  const pending = inFlight.get(path);
  if (pending) return pending;

  const request = getDownloadURL(storageRef(storage, path))
    .then((url) => {
      urls.set(path, url);
      return url as string | null;
    })
    .catch((error: any) => {
      if (error?.code === 'storage/object-not-found') {
        missing.set(path, Date.now());
        return null;
      }
      throw error; // network / permission: let the caller retry
    })
    .finally(() => {
      inFlight.delete(path);
    });

  inFlight.set(path, request);
  return request;
};

/** Call after the underlying object is deleted, so a stale URL is never served. */
export const invalidateAttachmentUrl = (path: string): void => {
  const url = urls.get(path);
  if (url) ratios.delete(url);
  urls.delete(path);
  missing.delete(path);
  inFlight.delete(path);
};
